"use client";

import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useMarketHint } from "@/components/market/market-hint-context";
import { setClientCookie } from "@/lib/cookies/client";
import {
	MARKET_COOKIE,
	MARKET_COOKIE_MAX_AGE,
	MARKET_HINT_COOKIE,
	type Market,
} from "@/lib/market/market";

/**
 * Manual market override — the SEO-safe user control over geo auto-detect.
 *
 * Modeled as a region switch: choosing "Worldwide" pins `market=global` and
 * routes to `/en` (the English/global experience); "Indonesia" pins
 * `market=indonesia` and routes to `/id`. Writes the override cookie client-side
 * and navigates — no full reload, the server re-renders with the new market.
 */
function setMarketCookie(market: Market) {
	setClientCookie(MARKET_COOKIE, market, {
		maxAgeSeconds: MARKET_COOKIE_MAX_AGE,
	});
}

// Mirror the choice into the resolved-market hint immediately so the rest of the
// page (MarketGate, AdSlotClient) reflects it without waiting for a re-resolve,
// and so the next edge-cached page paints the right framing. The hint used to be
// written by middleware; it is now client-owned (see market-hint-context.tsx).
function setMarketHintCookie(market: Market) {
	setClientCookie(MARKET_HINT_COOKIE, market);
}

function swapLocale(pathname: string, locale: "id" | "en"): string {
	const parts = pathname.split("/");
	if (parts[1] === "id" || parts[1] === "en") {
		parts[1] = locale;
		return parts.join("/") || `/${locale}`;
	}
	return `/${locale}${pathname}`;
}

export function MarketSwitcher({ locale }: { locale: string }) {
	const pathname = usePathname();
	const router = useRouter();
	const hint = useMarketHint();
	// Active highlight: track the resolved-market hint; seed from the locale
	// default (id ⇒ indonesia, en ⇒ global) before the provider resolves.
	const [active, setActive] = useState<Market>(
		locale === "id" ? "indonesia" : "global",
	);

	useEffect(() => {
		setActive(hint);
	}, [hint]);

	const choose = (market: Market) => {
		setMarketCookie(market);
		setMarketHintCookie(market);
		setActive(market);
		const target = market === "indonesia" ? "id" : "en";
		router.push(swapLocale(pathname || `/${locale}`, target));
		router.refresh();
	};

	const base =
		"transition-colors hover:text-primary cursor-pointer rounded px-1.5 py-0.5";
	const on = "text-foreground font-medium";
	const off = "text-muted-foreground";

	return (
		<div className="flex items-center gap-1 text-xs">
			<button
				type="button"
				onClick={() => choose("global")}
				className={`${base} ${active === "global" ? on : off}`}
				aria-pressed={active === "global"}
			>
				Worldwide
			</button>
			<span className="text-border">·</span>
			<button
				type="button"
				onClick={() => choose("indonesia")}
				className={`${base} ${active === "indonesia" ? on : off}`}
				aria-pressed={active === "indonesia"}
			>
				🇮🇩 Indonesia
			</button>
		</div>
	);
}
